import { Clock, Mail, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { ContactForm } from "@/components/ContactForm";
import { Eyebrow, Reveal } from "@/components/Reveal";

const strings = {
  fr: {
    eyebrow: "Contact",
    title: "Parlons de votre prochaine décision.",
    intro:
      "Un membre de notre équipe senior vous répond sous un jour ouvré. Toutes les premières discussions sont strictement confidentielles — le premier rendez-vous est gratuit et sans engagement.",
    expectTitle: "Ce qu'il faut attendre d'une première discussion",
    expectText:
      "La première conversation vise à comprendre la substance de votre situation, pas à vous vendre un discours standard : objectifs, calendrier, contreparties, juridictions concernées. S'il y a une adéquation, nous définissons une voie pratique avec un périmètre clair. Si un autre spécialiste est plus adapté, nous vous le dirons directement.",
    reasonsTitle: "Raisons fréquentes de nous contacter",
    reasons: [
      "Vous évaluez une expansion vers un nouveau marché ou une nouvelle juridiction.",
      "Vous avez besoin d'un soutien en structuration de capital ou en exécution de transaction.",
      "Votre direction cherche un partenaire de conseil senior pour un mandat confidentiel.",
      "Vous réévaluez gouvernance, reporting ou communication après un virage stratégique.",
      "Vous préparez une acquisition immobilière ou une transmission patrimoniale.",
    ],
    officesTitle: "Nos bureaux",
    offices: [
      { label: "Siège — Zurich", address: "Susenbergstrasse 104, 8044 Zürich, Suisse" },
    ],
    hours: "Lun – Ven, 9 h à 18 h",
    timesTitle: "Délais de réponse",
    times: [
      { label: "Réponse initiale", value: "Sous 24 h ouvrées" },
      { label: "Appel de découverte", value: "Sous 3 jours ouvrés" },
      { label: "Proposition", value: "5–10 jours ouvrés" },
      { label: "Démarrage du mandat", value: "2–4 semaines en général" },
    ],
    privacy: "En envoyant ce formulaire, vous acceptez notre politique de confidentialité. Toutes les informations sont traitées avec la plus stricte confidentialité.",
  },
  en: {
    eyebrow: "Contact",
    title: "Let's discuss your next decision.",
    intro:
      "A member of our senior team will respond within one business day. All initial discussions are strictly confidential — the first meeting is free and without obligation.",
    expectTitle: "What to expect from an initial discussion",
    expectText:
      "The first conversation is designed to understand the substance of your situation, not to deliver a standard pitch: objectives, timing, counterparties, jurisdictions involved. Where there is a fit, we define a practical path with a clear scope. If another specialist is more appropriate, we will say so directly.",
    reasonsTitle: "Common reasons clients contact us",
    reasons: [
      "You are evaluating expansion into a new market or jurisdiction.",
      "You need support with capital structuring or transaction execution.",
      "Your leadership wants a senior advisory partner for a confidential mandate.",
      "You are reassessing governance, reporting or communications after a strategic shift.",
      "You are preparing a property acquisition or a wealth transfer.",
    ],
    officesTitle: "Our offices",
    offices: [
      { label: "Headquarters — Zurich", address: "Susenbergstrasse 104, 8044 Zürich, Switzerland" },
    ],
    hours: "Mon – Fri, 9am to 6pm",
    timesTitle: "Response times",
    times: [
      { label: "Initial response", value: "Within 1 business day" },
      { label: "Discovery call", value: "Within 3 business days" },
      { label: "Proposal", value: "5–10 business days" },
      { label: "Engagement start", value: "2–4 weeks typical" },
    ],
    privacy: "By submitting this form you agree to our privacy policy. All information is handled with strict confidentiality.",
  },
  de: {
    eyebrow: "Kontakt",
    title: "Sprechen wir über Ihre nächste Entscheidung.",
    intro:
      "Ein Mitglied unseres Senior-Teams antwortet innerhalb eines Arbeitstages. Alle ersten Gespräche sind streng vertraulich — das erste Treffen ist kostenlos und unverbindlich.",
    expectTitle: "Was Sie vom ersten Gespräch erwarten dürfen",
    expectText:
      "Das erste Gespräch dient dazu, die Substanz Ihrer Situation zu verstehen — nicht darum, ein Standard-Pitch zu halten: Ziele, Zeitplan, Gegenparteien, betroffene Jurisdiktionen. Wenn es passt, definieren wir einen praktischen Weg mit klarem Umfang. Wenn ein anderer Spezialist besser passt, sagen wir es direkt.",
    reasonsTitle: "Häufige Gründe, uns zu kontaktieren",
    reasons: [
      "Sie prüfen eine Expansion in einen neuen Markt oder eine neue Jurisdiktion.",
      "Sie benötigen Unterstützung bei Kapitalstrukturierung oder Transaktionsausführung.",
      "Ihre Geschäftsleitung sucht einen seniorigen Beratungspartner für ein vertrauliches Mandat.",
      "Sie überdenken Governance, Reporting oder Kommunikation nach einer strategischen Wende.",
      "Sie bereiten einen Immobilienerwerb oder eine Vermögensübertragung vor.",
    ],
    officesTitle: "Unsere Standorte",
    offices: [
      { label: "Hauptsitz — Zürich", address: "Susenbergstrasse 104, 8044 Zürich, Schweiz" },
    ],
    hours: "Mo – Fr, 9 bis 18 Uhr",
    timesTitle: "Antwortzeiten",
    times: [
      { label: "Erste Antwort", value: "Innerhalb eines Arbeitstages" },
      { label: "Kennenlerngespräch", value: "Innerhalb von 3 Arbeitstagen" },
      { label: "Angebot", value: "5–10 Arbeitstage" },
      { label: "Mandatsbeginn", value: "In der Regel 2–4 Wochen" },
    ],
    privacy: "Mit dem Absenden dieses Formulars akzeptieren Sie unsere Datenschutzerklärung. Alle Informationen werden streng vertraulich behandelt.",
  },
};

export default function ContactPage() {
  const { lang } = useLang();
  const s = strings[lang];

  return (
    <div data-testid="contact-page">
      <div className="bg-[#FBF8F3] pb-16 pt-40 sm:pt-48">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#0A1F18] sm:text-5xl lg:text-6xl">
            {s.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5C6B64] sm:text-lg">{s.intro}</p>
        </div>
      </div>

      <section className="bg-[#FBF8F3] py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
          <div>
            <Reveal>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0A1F18] sm:text-3xl">
                {s.expectTitle}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-[#5C6B64]">{s.expectText}</p>
            </Reveal>
            <Reveal delay={0.1} className="mt-10">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#B36C45]">{s.reasonsTitle}</h3>
              <ul className="mt-5 space-y-3">
                {s.reasons.map((r) => (
                  <li key={r} className="flex items-start gap-3 border-b border-[#E4DDD0] pb-3 text-sm text-[#5C6B64]">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#C87D55]" />
                    {r}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15} className="mt-10 grid gap-px border border-[#E4DDD0] bg-[#E4DDD0]">
              {s.offices.map((o) => (
                <div key={o.label} className="bg-white p-6" data-testid="contact-office-zurich">
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#B36C45]">
                    <MapPin className="h-3.5 w-3.5" />
                    {o.label}
                  </div>
                  <p className="mt-3 text-sm text-[#0A1F18]">{o.address}</p>
                </div>
              ))}
              <div className="bg-white p-6">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#B36C45]">
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </div>
                <p className="mt-3 text-sm text-[#0A1F18]">contact@columbusmanagementconsulting.com</p>
              </div>
              <div className="bg-white p-6">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#B36C45]">
                  <Clock className="h-3.5 w-3.5" />
                </div>
                <p className="mt-3 text-sm text-[#0A1F18]">{s.hours}</p>
              </div>
            </Reveal>
          </div>

          <div>
            <ContactForm testidPrefix="contact-page-form" />
            <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-[#5C6B64]">{s.privacy}</p>
            <Reveal delay={0.1} className="mt-8">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#B36C45]">{s.timesTitle}</h3>
              <div className="mt-4 border border-[#E4DDD0] bg-white">
                {s.times.map((row) => (
                  <div key={row.label} className="flex items-center justify-between border-b border-[#E4DDD0] px-5 py-3.5 last:border-0">
                    <span className="text-sm text-[#5C6B64]">{row.label}</span>
                    <span className="font-mono text-xs text-[#0A1F18]">{row.value}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
